import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../primitives/dialog";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import { Label } from "../../primitives/label";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { toast } from "sonner";
import type { PullRequestResponse } from "@shared-core/data/feature-session-types";

interface OpenPRDialogProps {
  open: boolean;
  onClose: () => void;
  repository: string;
  branchName: string;
  onSuccess: (pr: PullRequestResponse) => void;
}

function branchToTitle(branchName: string): string {
  // "feature/improve-dashboard-charts" → "Improve dashboard charts"
  const name = branchName.replace(/^(feature|fix|chore)\//, "");
  const words = name.replace(/[-_]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function OpenPRDialog({
  open,
  onClose,
  repository,
  branchName,
  onSuccess,
}: OpenPRDialogProps) {
  const dataAdapter = useDataAdapter();
  const [title, setTitle] = useState(branchToTitle(branchName));
  const [body, setBody] = useState("");
  const [baseBranch, setBaseBranch] = useState("main");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const pr = await dataAdapter.openPullRequest(repository, branchName, {
        title: title.trim(),
        body: body.trim(),
        base_branch: baseBranch.trim() || "main",
      });
      toast.success(
        <span>
          PR created:{" "}
          <a
            href={pr.url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            #{pr.pr_number}
          </a>
        </span>,
      );
      onSuccess(pr);
      onClose();
    } catch (error) {
      console.error("Error creating PR:", error);
      toast.error("Failed to create pull request");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open Pull Request</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="pr-title">Title</Label>
            <Input
              id="pr-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pr-body">Description</Label>
            <Textarea
              id="pr-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe the changes..."
              rows={4}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pr-base">Base branch</Label>
            <Input
              id="pr-base"
              value={baseBranch}
              onChange={(e) => setBaseBranch(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()}>
            {submitting ? "Creating..." : "Create PR"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
